import { useState } from 'react';
import { useStore } from '../stores/useStore';
import { getPlayer } from '../lib/api';
import styles from './ProgressPage.module.css';

export function ProgressPage() {
  const player = useStore((s) => s.player);
  const skillProgress = useStore((s) => s.skillProgress);
  const setPlayer = useStore((s) => s.setPlayer);
  const [refreshing, setRefreshing] = useState(false);

  const refreshPlayer = async () => {
    if (!player) return;
    setRefreshing(true);
    try {
      const updated = await getPlayer(player.id);
      setPlayer(updated);
    } catch (err) {
      console.error('Failed to refresh player:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const skills = Object.values(skillProgress);
  const totalQuestions = skills.reduce((sum, s) => sum + s.questionsAnswered, 0);
  const totalCorrect = skills.reduce((sum, s) => sum + s.correctAnswers, 0);
  const overallAccuracy = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;

  const getSkillLevel = (theta: number): string => {
    if (theta < -1.5) return 'Beginner';
    if (theta < -0.5) return 'Learning';
    if (theta < 0.5) return 'Competent';
    if (theta < 1.5) return 'Proficient';
    return 'Expert';
  };

  const getSkillColor = (theta: number): string => {
    if (theta < -1) return '#ef4444';
    if (theta < 0) return '#f97316';
    if (theta < 1) return '#eab308';
    return '#22c55e';
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Your Progress</h1>

      {/* Player Stats Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Overall Stats</h2>
          <button
            onClick={refreshPlayer}
            className={styles.refreshButton}
            disabled={refreshing}
          >
            {refreshing ? '...' : '↻'}
          </button>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>
              {player?.rating.ordinal.toFixed(0) || 0}
            </div>
            <div className={styles.statLabel}>Rating</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>
              {player?.stats.gamesPlayed || 0}
            </div>
            <div className={styles.statLabel}>Games</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{totalQuestions}</div>
            <div className={styles.statLabel}>Questions</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>
              {overallAccuracy.toFixed(0)}%
            </div>
            <div className={styles.statLabel}>Accuracy</div>
          </div>
        </div>

        <div className={styles.thetaDisplay}>
          <span className={styles.thetaLabel}>Ability (θ)</span>
          <span className={styles.thetaValue}>
            {player?.irt.theta.toFixed(2) || '0.00'}
          </span>
          <span className={styles.thetaLevel}>
            {getSkillLevel(player?.irt.theta || 0)}
          </span>
        </div>
      </div>

      {/* Skills Progress */}
      <div className={styles.skillsSection}>
        <h2 className={styles.sectionTitle}>Skills by Category</h2>

        {skills.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No skill data yet. Take a quiz to start tracking your progress!</p>
          </div>
        ) : (
          <div className={styles.skillsGrid}>
            {skills.map((skill) => (
              <div key={skill.category} className={styles.skillCard}>
                <div className={styles.skillHeader}>
                  <span className={styles.skillName}>{skill.category}</span>
                  <span
                    className={styles.skillLevel}
                    style={{ color: getSkillColor(skill.estimatedTheta) }}
                  >
                    {getSkillLevel(skill.estimatedTheta)}
                  </span>
                </div>

                <div className={styles.skillBar}>
                  <div
                    className={styles.skillBarFill}
                    style={{
                      width: `${Math.min(100, Math.max(0, (skill.estimatedTheta + 2) * 25))}%`,
                      background: getSkillColor(skill.estimatedTheta),
                    }}
                  />
                </div>

                <div className={styles.skillStats}>
                  <span>{skill.questionsAnswered} questions</span>
                  <span>{(skill.accuracy * 100).toFixed(0)}% accuracy</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
